import { useCallback, useEffect, useState } from 'react';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  Progress,
  SupportButton,
} from '@backstage/core-components';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';

type ServiceStatus = {
  // This shape mirrors the JSON returned by the backend status plugin.
  id: string;
  name: string;
  status: 'operational' | 'degraded' | 'unavailable';
  statusCode?: number;
  latencyMs: number;
  error?: string;
};

type StatusResponse = {
  checkedAt: string;
  services: ServiceStatus[];
};

const isServiceOperational = (status: ServiceStatus['status']) =>
  status === 'operational';

export const ApiStatusPage = () => {
  // Backstage's fetch API forwards the signed-in identity token to the backend.
  const fetchApi = useApi(fetchApiRef);
  const [result, setResult] = useState<StatusResponse>();
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    try {
      // The browser calls Backstage, not the MSP/CPO APIs directly.
      // That keeps Entra client secrets and access tokens on the backend.
      const response = await fetchApi.fetch('/api/api-status');
      if (!response.ok) {
        throw new Error(`Status endpoint returned ${response.status}`);
      }
      setResult((await response.json()) as StatusResponse);
      setError(undefined);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Status check failed.');
    }
  }, [fetchApi]);

  useEffect(() => {
    // Run once when the page opens, then repeat every five minutes.
    void refresh();
    const interval = window.setInterval(() => void refresh(), 300_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  return (
    <Page themeId="home">
      <Header title="API Status" subtitle="Authenticated MSP and CPO health checks" />
      <Content>
        <ContentHeader title="Service health">
          <SupportButton>Checks run automatically every five minutes.</SupportButton>
        </ContentHeader>
        {/* Show a request error separately from an API service outage. */}
        {error && <p role="alert">{error}</p>}
        {!result && !error && <Progress />}
        {result && (
          <>
            <p>Last checked: {new Date(result.checkedAt).toLocaleString()}</p>
            <p>
              Checks run in the backend every five minutes. This page displays the latest
              cached result, and the same result is available to the observability metrics
              pipeline.
            </p>
            {/* Add columns here when the backend returns more status fields. */}
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                  <th>HTTP status</th>
                  <th>Latency</th>
                </tr>
              </thead>
              <tbody>
                {result.services.map(service => (
                  <tr key={service.id}>
                    <td>{service.name}</td>
                    <td>
                      <span
                        aria-label={
                          isServiceOperational(service.status)
                            ? 'Working'
                            : 'Failure'
                        }
                        title={
                          isServiceOperational(service.status)
                            ? 'Working'
                            : 'Failure'
                        }
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          marginRight: 8,
                          borderRadius: '50%',
                          backgroundColor: isServiceOperational(service.status)
                            ? '#2e7d32'
                            : '#d32f2f',
                          verticalAlign: 'middle',
                        }}
                      />
                      {service.status}
                    </td>
                    <td>{service.statusCode ?? service.error ?? '—'}</td>
                    <td>{service.latencyMs} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Content>
    </Page>
  );
};
